import {
  Bar,
  BarChart as BarChartRechart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart as LineChartRechart,
  PieChart,
  Pie as PieRechart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const COLORS = [
  "rgb(158,1,66,0.7)", // red
  "rgb(213,62,79,0.7)", // red
  "rgb(244,109,67,0.7)", // orange
  "rgb(253,174,97,0.7)", // orange
  "rgb(254,224,139,0.7)", // yellow
  "rgb(230,245,152,0.7)", // yellow
  "rgb(171,221,164,0.7)", // green
  "rgb(102,194,165,0.7)", // green
  "rgb(50,136,189,0.7)", // blue
  "rgb(94,79,162,0.7)", // blue
  "rgba(0,0,145,255)", // dark blue
];

const PieTooltip = ({ active, payload, unit }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white shadow-lg">
        <div className="text-text-mention bg-background-grey-hover w-full px-4 py-2 text-xs">
          <p className="label">{payload[0].payload.name}</p>
        </div>
        <div className="space-y-2 p-4">
          {payload.map((p, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="mr-2 h-2 w-4" style={{ backgroundColor: p.payload.fill }} />
              <p className="text-xs font-bold">{unit ? `${p.value} ${unit}` : p.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export const Pie = ({ data, dataKey = "value", nameKey = "name", cx = "50%", cy = "50%", outerRadius = "100%", innerRadius = "50%", paddingAngle = 0.2, unit = "" }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <PieRechart data={data} dataKey={dataKey} nameKey={nameKey} cx={cx} cy={cy} outerRadius={outerRadius} innerRadius={innerRadius} paddingAngle={paddingAngle}>
          {data.map((entry, i) => (
            <Cell key={`cell-${i}`} fill={entry.name === "Autres" ? "rgba(0,0,0,0.5)" : entry.color ? entry.color : COLORS[i % COLORS.length]} />
          ))}
        </PieRechart>
        <Tooltip content={<PieTooltip unit={unit} />} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const LineChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((acc, p) => acc + p.value, 0);
    return (
      <div className="bg-white shadow-lg">
        <div className="text-text-mention bg-background-grey-hover w-full px-4 py-2 text-xs">
          <p className="label">{`${label} - ${total}`}</p>
        </div>
        <div className="space-y-2 p-4">
          {payload.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-4">
              <div className="flex items-center">
                <div className="mr-2 h-2 w-4" style={{ backgroundColor: p.stroke }} />
                <p className="truncate text-xs font-bold">{p.name}</p>
              </div>
              <p className="text-xs font-bold">{p.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export const LineChart = ({ data, dataKey = ["value"], nameKey = "name", color = "", type = "", legend = true }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChartRechart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={nameKey} style={{ fontSize: "12px", color: "#666666" }} />
        <YAxis />
        <Tooltip content={<LineChartTooltip />} />
        {legend && <Legend />}
        {dataKey.map((k, i) => (
          <Line key={i} type={type} dataKey={k} stroke={color[i % color.length]} />
        ))}
      </LineChartRechart>
    </ResponsiveContainer>
  );
};

const BarCharLabelList = ({ value, x, y, width, height }) => {
  return (
    <text
      className="text-xs"
      x={x + width / 2}
      y={height > 20 ? y + height / 2 : y - 4}
      fill={height > 20 ? "white" : "black"}
      dy={height > 20 ? null : -10}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {value >= 10000 ? `${Math.round(value / 1000)}k` : value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
    </text>
  );
};

const BarChartTooltip = ({ active, payload, label, nameFormatter, tooltipName, tooltipValueFormatter }) => {
  if (active && payload && payload.length) {
    const formattedLabel = nameFormatter ? nameFormatter(label) : label;
    const formattedValue = tooltipValueFormatter ? tooltipValueFormatter(payload[0].value) : payload[0].value;
    const resolvedName = tooltipName || payload[0].name;
    return (
      <div className="bg-white shadow-lg">
        <div className="text-text-mention bg-background-grey-hover w-full px-4 py-2 text-xs">
          <p className="label">{formattedLabel}</p>
        </div>
        <div className="space-y-2 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center">
              <div className="mr-2 h-2 w-4" style={{ backgroundColor: payload[0].fill }} />
              <p className="truncate text-xs font-bold">{resolvedName}</p>
            </div>
            <p className="text-xs font-bold">{formattedValue}</p>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const BarChart = ({ data, dataKey = "Volume", nameKey = "name", color = "#000091", nameFormatter, tooltipName, tooltipValueFormatter }) => {
  if (!data || !data.length) {
    return;
  }

  const formattedData = nameFormatter ? data.map((item) => ({ ...item, [nameKey]: nameFormatter(item[nameKey]) })) : data;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChartRechart data={formattedData}>
        <XAxis dataKey={nameKey} tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
        <Tooltip content={<BarChartTooltip nameFormatter={nameFormatter} tooltipName={tooltipName} tooltipValueFormatter={tooltipValueFormatter} />} />
        <Bar dataKey={dataKey} fill={color} barSize={30} radius={[2, 2, 0, 0]}>
          <LabelList dataKey={dataKey} content={<BarCharLabelList />} />
        </Bar>
      </BarChartRechart>
    </ResponsiveContainer>
  );
};

const StackedBarChartTooltip = ({ active, payload, label, seriesLabelMap = {} }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((acc, p) => acc + p.value, 0);
    return (
      <div className="bg-white shadow-lg">
        <div className="text-text-mention bg-background-grey-hover w-full px-4 py-2 text-xs">
          <p className="label">{`${label} - ${total}`}</p>
        </div>
        <div className="space-y-2 p-4">
          {payload.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-4">
              <div className="flex items-center">
                <div className="mr-2 h-2 w-4" style={{ backgroundColor: p.fill }} />
                <p className="truncate text-xs font-bold">{seriesLabelMap[p.name] || p.name}</p>
              </div>
              <p className="text-xs font-bold">{p.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const StackedBarChartLegend = ({ payload, seriesLabelMap = {} }) => {
  return (
    <div className="flex flex-wrap justify-end gap-4 pb-4">
      {payload.map((entry, index) => (
        <div key={`item-${index}`} className="flex items-center gap-2">
          <div className="h-2 w-4" style={{ backgroundColor: entry.color }}></div>
          <p className="truncate text-xs">{seriesLabelMap[entry.value] || entry.value}</p>
        </div>
      ))}
    </div>
  );
};

export const StackedBarchart = ({ data, dataKey = ["Volume"], nameKey = "name", color = COLORS, legend = true, seriesLabelMap = {} }) => {
  if (!data || !data.length) {
    return;
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChartRechart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey={nameKey} style={{ fontSize: "12px", color: "#666666" }} />
        <YAxis style={{ fontSize: "12px", color: "#666666" }} />
        <Tooltip content={<StackedBarChartTooltip seriesLabelMap={seriesLabelMap} />} />
        {legend && <Legend verticalAlign="top" content={<StackedBarChartLegend seriesLabelMap={seriesLabelMap} />} />}
        {dataKey.map((k, i) => (
          <Bar key={i} dataKey={k} stackId="a" fill={k === "Autres" ? "rgba(0,0,0,0.5)" : color[i % color.length]} />
        ))}
      </BarChartRechart>
    </ResponsiveContainer>
  );
};

export default Pie;
